import { ConditionType, Operator } from '@de/types/enums';
import type {
  Step,
  Option,
  BranchingLogic,
  Component,
  MultiQuestion,
  Question,
} from '@de/types/types';
import type { StepData } from '@de/providers/dynamicExperienceReducer';

type GetNextStepProps = {
  currentStepData: Partial<StepData> & Record<string, unknown>;
  selectedOptions: Option[];
  currentStep: Step;
  allSteps: Step[];
};

const META_LEAD_FIELD = '_meta.leadFieldAssociated';
const META_CUSTOM_FIELD = '_meta.customField';

const getSubsequentStep = (
  branchingLogic: BranchingLogic,
  allSteps: Step[],
): Step | null => {
  return allSteps.find((s: Step) => s.id === branchingLogic.nextId) || null;
};

const hasNonEmptySlug = (opt: Option): boolean =>
  typeof opt.slug === 'string' && opt.slug.length > 0;

const hasNonEmptyValue = (opt: Option): boolean =>
  opt.value !== undefined && opt.value !== null && String(opt.value).length > 0;

/** True when the option contributes neither a value nor a slug. */
const isBlankOption = (opt: Option): boolean =>
  !hasNonEmptyValue(opt) && !hasNonEmptySlug(opt);

const optionDimKey = (opt: Option): string | undefined => {
  if (
    typeof opt.leadFieldAssociated === 'string' &&
    opt.leadFieldAssociated.length > 0
  ) {
    return opt.leadFieldAssociated;
  }
  if (typeof opt.customField === 'string' && opt.customField.length > 0) {
    return opt.customField;
  }
  return undefined;
};

const combinationHasEmptyToken = (combination: string[] | undefined): boolean =>
  Array.isArray(combination) && combination.some((v) => v === '');

const addSlot = (slots: Set<string>, value: unknown): void => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      slots.add(trimmed);
    }
  }
};

/**
 * Walk a Puck DSL payload for author-declared field handles used as
 * combination slots (`_meta.leadFieldAssociated` / `_meta.customField`).
 */
const collectSlotsFromDslData = (data: unknown, slots: Set<string>): void => {
  if (!data || typeof data !== 'object') return;
  if (Array.isArray(data)) {
    for (const item of data) {
      collectSlotsFromDslData(item, slots);
    }
    return;
  }
  const record = data as Record<string, unknown>;
  addSlot(slots, record[META_LEAD_FIELD]);
  addSlot(slots, record[META_CUSTOM_FIELD]);
  const props = record.props;
  if (props && typeof props === 'object' && !Array.isArray(props)) {
    const propsRecord = props as Record<string, unknown>;
    addSlot(slots, propsRecord[META_LEAD_FIELD]);
    addSlot(slots, propsRecord[META_CUSTOM_FIELD]);
  }
  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') {
      collectSlotsFromDslData(value, slots);
    }
  }
};

/**
 * Step-declared slots for empty-token projection.
 *
 * Sources (union, order-independent):
 * - `leadFieldAssociated` / `customField` on the step and child questions
 * - per-option lead/custom handles on Question `options`
 * - `_meta.leadFieldAssociated` / `_meta.customField` in a COMPONENT DSL tree
 *
 * When nothing is declared, returns `[]` — callers then only normalize
 * blank *present* options and do not invent missing dims.
 *
 * Narrowing uses field-name overlap across the `Step` union (`DCS` /
 * `MultiQuestion` share `stepQuestions`; `Component` carries `dslConfig`).
 * A rename on one member would silently stop collecting that source.
 */
export const collectExpectedSlotsFromStep = (
  step: Step | undefined,
): string[] => {
  if (!step) return [];
  const slots = new Set<string>();
  addSlot(slots, step.leadFieldAssociated);
  addSlot(slots, step.customField);

  // Field-overlap casts — see docstring above.
  const asMulti = step as MultiQuestion;
  if (Array.isArray(asMulti.stepQuestions)) {
    for (const question of asMulti.stepQuestions) {
      addSlot(slots, question.leadFieldAssociated);
      addSlot(slots, question.customField);
      for (const opt of question.options || []) {
        addSlot(slots, opt.leadFieldAssociated);
        addSlot(slots, opt.customField);
      }
    }
  }
  if (Array.isArray(asMulti.groupedQuestions)) {
    for (const group of asMulti.groupedQuestions) {
      for (const question of group.questions || []) {
        addSlot(slots, question.leadFieldAssociated);
        addSlot(slots, question.customField);
        for (const opt of question.options || []) {
          addSlot(slots, opt.leadFieldAssociated);
          addSlot(slots, opt.customField);
        }
      }
    }
  }

  const asQuestion = step as Question;
  if (Array.isArray(asQuestion.options)) {
    for (const opt of asQuestion.options) {
      addSlot(slots, opt.leadFieldAssociated);
      addSlot(slots, opt.customField);
    }
  }

  const dslData = (step as Component).dslConfig?.data;
  if (dslData !== undefined) {
    collectSlotsFromDslData(dslData, slots);
  }

  return Array.from(slots);
};

/**
 * Evaluation-time virtualization for Combination Selected empty tokens.
 *
 * - Present options with null/undefined (and no slug) are treated as `''`
 *   for matching only — flowState / selection are never mutated.
 * - When `condition.combination` includes `''`, missing *step-declared*
 *   slots are projected as virtual empty options (no hardcoded triad;
 *   present option dims are skipped via `presentDims`, so they are not
 *   re-added to the expected set).
 */
export const projectOptionsForEmptyCombinationSlots = (
  options: Option[],
  combination: string[] | undefined,
  stepDeclaredSlots: readonly string[] = [],
): Option[] => {
  const projected = options.map((opt) =>
    isBlankOption(opt) ? { ...opt, value: '' } : opt,
  );

  if (!combinationHasEmptyToken(combination)) {
    return projected;
  }

  const presentDims = new Set(
    projected
      .map(optionDimKey)
      .filter((dim): dim is string => typeof dim === 'string'),
  );

  for (const dim of stepDeclaredSlots) {
    if (!presentDims.has(dim)) {
      projected.push({
        optionId: dim,
        text: '',
        value: '',
        leadFieldAssociated: dim,
      });
    }
  }

  return projected;
};

// Helper function to evaluate if a condition matches.
// `currentStep` / optional `stepDeclaredSlots` supply step-declared
// lead/custom field handles for empty-token combination projection;
// omit both to fall back to blank present-option normalization only
// (no invented missing dims). Pass precomputed `stepDeclaredSlots`
// when evaluating many rules for the same step (e.g. `getNextStep`).
export const evaluateCondition = (
  branchingLogic: BranchingLogic,
  currentStepData: Partial<StepData> & Record<string, unknown>,
  selectedOptions: Option[],
  currentStep?: Step,
  stepDeclaredSlots?: readonly string[],
): boolean => {
  const { condition } = branchingLogic;

  if (condition.type === ConditionType.DEFAULT) {
    return true; // DEFAULT
  }

  if (
    condition.type !== ConditionType.OPTION_SELECTED &&
    condition.type !== ConditionType.GROUP_SELECTED &&
    condition.type !== ConditionType.COMBINATION_SELECTED
  ) {
    return false;
  }

  // Extract the option(s) from currentStepData
  const currentStepOptions = currentStepData.option;

  // Extract all the selected options
  const allOptions = selectedOptions;

  // Handle case where no option is selected
  if (!currentStepOptions || !allOptions) {
    return false;
  }

  // Determine if we're dealing with a single option or array of options in the current step
  const isArray = Array.isArray(currentStepOptions);
  const singleOption = isArray
    ? (currentStepOptions as Option[])[0]
    : (currentStepOptions as Option);
  const options = isArray
    ? (currentStepOptions as Option[])
    : [currentStepOptions as Option];

  // CONTAINS_ANY is used to evaluate all the selected options
  switch (condition.type) {
    case ConditionType.GROUP_SELECTED:
      switch (condition.operator) {
        // Contains any group
        case Operator.CONTAINS_ANY:
          return allOptions.some((opt) => opt.group === condition.group);
        // Contains all
        case Operator.CONTAINS:
          return isArray
            ? options.every((opt) => opt.group === condition.group)
            : singleOption?.group === condition.group;
        // Group equals
        case Operator.EQUALS:
          return singleOption?.group === condition.group;
        // Group does not equal
        case Operator.NOT_EQUALS:
          return singleOption?.group !== condition.group;
        default:
          return false;
      }
    case ConditionType.COMBINATION_SELECTED: {
      // DSL `COMBINATION_SELECTED` conditions are authored against
      // canonical slugs (e.g. `'bachelors'`, `'nursing-and-healthcare'`).
      // DSL Selects, however, are usually bound to a non-slug field via
      // `_bind.optionValueField` because cascading Mobius queries need
      // the numeric `criteria.degreeId`. The bridge surfaces both: the
      // bound field on `opt.value` and the canonical slug on `opt.slug`
      // (when the source carries one). The matcher accepts either so
      // authors never have to choose between cascading queries and
      // slug-based branching.
      //
      // Empty-token follow-up: when authors encode blank dims as `''`
      // in `combination`, missing / nullish expected lead fields are
      // projected as empty at evaluation time only (never written into
      // flowState). Expected slots come from the current step, not a
      // hardcoded triad. See `projectOptionsForEmptyCombinationSlots`.
      const slots =
        stepDeclaredSlots ?? collectExpectedSlotsFromStep(currentStep);
      const matchAllOptions = projectOptionsForEmptyCombinationSlots(
        allOptions,
        condition.combination,
        slots,
      );
      const matchStepOptions = projectOptionsForEmptyCombinationSlots(
        options,
        condition.combination,
        slots,
      );
      // Build the comparable-key list for an option. Skip `null` /
      // `undefined` values rather than coercing them to the literal
      // strings `'null'` / `'undefined'` -- a DSL that accidentally
      // authored `combination: ['null']` would otherwise match a
      // value-less option, which is never the author's intent.
      // Blank options are normalized to `value: ''` above so authored
      // empty combination tokens can match missing/nullish dims.
      // Empty-string slugs are already rejected upstream by
      // `resolveSourceSlug`, but mirror that here defensively.
      const optionKey = (opt: Option): string[] => {
        const keys: string[] = [];
        if (opt.value !== undefined && opt.value !== null) {
          keys.push(String(opt.value));
        }
        if (typeof opt.slug === 'string' && opt.slug.length > 0) {
          keys.push(opt.slug);
        }
        return keys;
      };
      const matchesCombination = (combinationValue: string): boolean =>
        matchAllOptions.some((opt) =>
          optionKey(opt).includes(combinationValue),
        );
      // Empty `condition.combination` is treated as a no-match for the
      // existence-style operators. `[].every()` returns `true`, which
      // would otherwise vacuously satisfy CONTAINS_ANY and cause an
      // empty-authored branch to always fire. EQUALS keeps its own
      // sorted-list equality check (which already handles `[]`
      // correctly), and CONTAINS / NOT_EQUALS naturally short-circuit
      // on an empty/absent combination via `?? false`.
      const hasCombination =
        Array.isArray(condition.combination) &&
        condition.combination.length > 0;
      switch (condition.operator) {
        case Operator.CONTAINS_ANY: {
          // Check if all value/slug and optionIds in combination exist
          const allValuesExist = hasCombination
            ? condition.combination!.every(matchesCombination)
            : false;

          // optionIds must reflect real selections — never virtual blanks
          // projected for empty-token combination matching (`optionId: dim`).
          const allOptionIdsExist =
            condition.optionIds && condition.optionIds.length > 0
              ? condition.optionIds.every((combinationId) =>
                  allOptions.some((opt) => opt.optionId === combinationId),
                )
              : true;
          return allValuesExist && allOptionIdsExist;
        }
        // Check if any selected option value/slug is in the combination
        case Operator.CONTAINS:
          return matchStepOptions.some((opt) =>
            optionKey(opt).some(
              (k) => condition.combination?.includes(k) ?? false,
            ),
          );
        // For EQUALS, check if selected values exactly match the combination
        case Operator.EQUALS: {
          // Symmetric "either projection matches" semantics, matching
          // how the other operators handle slug-aware comparisons:
          //   1. Try the bound-value projection first (legacy DSLs
          //      author EQUALS against whatever was bound to
          //      `_bind.optionValueField`, e.g. numeric ids).
          //   2. If that doesn't match AND every selected option has
          //      a slug, try the slug projection (new id-bound +
          //      slug-authored flows).
          // Order matters here: trying value first preserves backward
          // compatibility for any production DSL whose EQUALS
          // combination is keyed on the bound value, even if every
          // option happens to have a slug companion.
          //
          // Nullish-value policy mirrors `optionKey` above: only build
          // the value projection when every selected option has a
          // non-nullish value. Otherwise `String(undefined)` →
          // `'undefined'` would let a DSL that accidentally authored
          // `combination: ['undefined']` match a value-less option,
          // which is never the author's intent. Blank options are
          // normalized to `value: ''` before this check.
          const combinationValues = (condition.combination || [])
            .slice()
            .sort();
          const matchesProjection = (projected: string[]): boolean => {
            const sorted = projected.slice().sort();
            return JSON.stringify(sorted) === JSON.stringify(combinationValues);
          };
          const everyHasValue = matchStepOptions.every(
            (opt) => opt.value !== undefined && opt.value !== null,
          );
          if (everyHasValue) {
            const valueProjection = matchStepOptions.map((opt) =>
              String(opt.value),
            );
            if (matchesProjection(valueProjection)) return true;
          }
          const everyHasSlug = matchStepOptions.every((opt) => !!opt.slug);
          if (everyHasSlug) {
            const slugProjection = matchStepOptions.map((opt) =>
              String(opt.slug),
            );
            return matchesProjection(slugProjection);
          }
          return false;
        }
        // Check if no selected option value/slug is in the combination
        case Operator.NOT_EQUALS:
          return !matchStepOptions.some((opt) =>
            optionKey(opt).some(
              (k) => condition.combination?.includes(k) ?? false,
            ),
          );
        default:
          return false;
      }
    }
    case ConditionType.OPTION_SELECTED:
      switch (condition.operator) {
        // Check if any selected option matches the condition
        case Operator.CONTAINS_ANY:
          return allOptions.some(
            (opt) =>
              // check for deep equality
              opt.optionId === condition.optionId &&
              opt.value === condition.value,
          );
        // is equal to the selected option
        case Operator.EQUALS:
          return options.some(
            (opt) =>
              opt.optionId === condition.optionId ||
              opt.value === condition.value,
          );
        // is not equal to any ofthe selected option
        case Operator.NOT_EQUALS:
          return !allOptions.some(
            (opt) =>
              opt.optionId === condition.optionId ||
              opt.value === condition.value,
          );
        // is greater than the selected option
        case Operator.GREATER_THAN:
          return options.some(
            (opt) => Number(opt.value) > Number(condition.value),
          );
        // is less than the selected option
        case Operator.LESS_THAN:
          return options.some(
            (opt) => Number(opt.value) < Number(condition.value),
          );
        // contains - check if any selected option matches
        case Operator.CONTAINS:
          return options.some((opt) => {
            // If condition specifies optionId, the option must match that optionId
            const optionIdMatches =
              !condition.optionId || opt.optionId === condition.optionId;

            // If condition specifies value, the option value must contain that value
            const valueContains =
              !condition.value ||
              String(opt.value).includes(String(condition.value));

            // Both conditions must be satisfied
            return optionIdMatches && valueContains;
          });
        default:
          return false;
      }

    default:
      return false;
  }
};

const getNextStep = ({
  selectedOptions,
  currentStepData,
  currentStep,
  allSteps,
}: GetNextStepProps): Step | null => {
  // If no branching logic, return null (end of flow)
  if (!currentStep.branchingLogic || currentStep.branchingLogic.length === 0) {
    return null;
  }
  // Collect once per step — combination rules may walk a large DSL tree.
  const stepDeclaredSlots = collectExpectedSlotsFromStep(currentStep);
  // Find the first matching branch (excluding DEFAULT)
  const matchingBranch = currentStep.branchingLogic.find(
    (branchingLogic) =>
      branchingLogic.condition.type !== ConditionType.DEFAULT &&
      evaluateCondition(
        branchingLogic,
        currentStepData,
        selectedOptions,
        currentStep,
        stepDeclaredSlots,
      ),
  );

  if (matchingBranch) {
    return getSubsequentStep(matchingBranch, allSteps);
  }

  // Fall back to DEFAULT branch if no other conditions match
  const defaultBranch = currentStep.branchingLogic.find(
    (branchingLogic) => branchingLogic.condition.type === ConditionType.DEFAULT,
  );

  if (defaultBranch) {
    return getSubsequentStep(defaultBranch, allSteps);
  }

  return null;
};

export default getNextStep;
